import { useState, useCallback, useEffect, useRef } from "react";
import general, { redux } from "../../../../../general";
import Dialog from "../../../../../lib/Dialog";

/**
 * 管理群组元数据（标题、公告、成员、是否管理员）、会话激活、红包顶部消息。
 *
 * @returns {{
 *   title, announcement, gameid, isAppointed, isDisplayHonor,
 *   packSerialcode, packAlert, setPackAlert,
 *   visible, redenvelopeTopMsg,
 *   nickMembers, findAccountFilterByAccount,
 *   updateMsg, gotoChat, initOnce,
 * }}
 */
export default function useTeamChatMeta({ store, nim, tid, sessionId }) {
  const [title,           setTitle]           = useState();
  const [announcement,    setAnnouncement]     = useState(null);
  const [gameid,          setGameid]           = useState();
  const [isAppointed,     setIsAppointed]      = useState(false);
  const [isDisplayHonor,  setIsDisplayHonor]   = useState(false);
  const [packSerialcode,  setPackSerialcode]   = useState("");
  const [packAlert,       setPackAlert]        = useState(false);
  const [visible,         setVisible]          = useState(false);
  const [redenvelopeTopMsg, setRedenvelopeTopMsg] = useState();
  // 非成员荣誉数据（account → { honor_icon }）存入 state，API 返回后触发重渲染
  const [extraHonorMap,   setExtraHonorMap]    = useState({});
  const [membersVersion,  setMembersVersion]   = useState(0); // 成员列表更新时触发重渲染
  const [isMuted,         setIsMuted]          = useState(false);

  // 每个 hook 实例独立的成员数据（避免模块级变量跨群聊污染）
  const membersRef          = useRef([]);
  const nickMembersRef      = useRef([]);
  const fetchingAccountsRef = useRef(new Set());
  // gotoChat 内 setTimeout 的句柄，组件卸载时清理
  const gotoChatTimerRef    = useRef(null);
  // nickMembers 版本号：getYXNickMembers 完成后触发重渲染，确保昵称即时更新
  const [nickMembersVersion, setNickMembersVersion] = useState(0);

  // ── 拉取最新消息 ──────────────────────────────────────────────────────────
  // 前台同步时调用（页面切回前台）：仅追加最新消息，不清空 store，保留滚动位置。
  const updateMsg = useCallback(() => {
    store.msgStore.getHistoryMsgActive({ sessionId, limit: 100 }).catch(() => {});
  }, [store, sessionId]);

  // ── 激活/选中当前会话 ────────────────────────────────────────────────────
  // selectSession 只在首次进入时调用，避免返回时触发所有 observer（含头像组件）重渲染
  const gotoChat = useCallback((isFirst = false) => {
    if (isFirst) {
      store.uiStore.selectSession(sessionId);
    }
    clearTimeout(gotoChatTimerRef.current);
    gotoChatTimerRef.current = setTimeout(() => {
      store.sessionStore.insertSessionActive("team", tid, true);
    }, 100);
    store.sessionStore.resetSession(sessionId).then(() => {
      redux.dispatch({ type: "updateChatUnread", data: { updateChatUnread: true } });
    });
  }, [store, sessionId, tid]);

  // ── 群成员列表 ─────────────────────────────────────────────────────────────
  const getMembers = useCallback(() => {
    general.axios.post("/base/yunxin/teamListUsers", { tid }).then((res) => {
      const { status, data } = res.data;
      if (status.succeed) {
        membersRef.current = data;
        setMembersVersion(v => v + 1); // 触发重渲染，荣耀图标立即显示
      }
    });
  }, [tid]);

  const getYXNickMembers = useCallback(() => {
    store.teamMemberStore.getTeamMemberActive(tid).then((res) => {
      nickMembersRef.current = res;
      setNickMembersVersion(v => v + 1); // 触发重渲染，昵称立即更新
      // 初始化当前用户禁言状态
      const myAccount = localStorage.getItem("yx_account");
      const me = res.find((m) => m.account === myAccount);
      if (me) setIsMuted(!!me.mute);
    });
  }, [store, tid]);

  // ── 群信息（公告、标题、礼包码） ─────────────────────────────────────────
  const getTeamInfo = useCallback(() => {
    general.axios.post("/base/yunxin/getClusterInfo", { tid }).then((res) => {
      const { data, status } = res.data;
      if (status.succeed) {
        setTitle(data.info.tname);
        setAnnouncement(data.info.announcement.list);
        if (data.info.pack_serialcode?.length > 0) setPackSerialcode(data.info.pack_serialcode);
        setGameid(data.info.gameid);
        setIsAppointed(data.info.is_appoint_intendant);
        setIsDisplayHonor(data.info.is_display_honor === 0);
        getMembers();
      } else {
        Dialog.error(status.error_desc);
      }
    });
  }, [tid, getMembers]);

  // ── 红包顶部提示 ──────────────────────────────────────────────────────────
  const getRedenvelopeTopMess = useCallback(() => {
    general.axios.post("/getRedenvelopeTopMess", { tid }).then((res) => {
      const { status, data } = res.data;
      if (status.succeed) {
        if (!data || Object.keys(data).length === 0 || parseInt(data.is_disappear_pop) === 1) {
          setVisible(false);
          return;
        }
        setVisible(true);
        setRedenvelopeTopMsg(data);
      }
    });
  }, [tid]);

  // ── 查找荣誉图标 ──────────────────────────────────────────────────────────
  // useCallback 保证函数引用稳定，防止每次父组件渲染都破坏 MessageRow 的 React.memo。
  // extraHonorMap 在 deps 中：API 返回后 setState → 函数引用更新 → MessageRow 拿到新数据。
  const findAccountFilterByAccount = useCallback((account) => {
    if (!membersRef.current.length) return null;
    if (account === localStorage.getItem("yx_account") && !isDisplayHonor) return null;
    // 优先从 members（群成员列表）中查找
    const allItems = membersRef.current.flatMap((item) => item.list || []);
    const result   = allItems.find((item) => item.account === account);
    if (result) return result;
    // 不在群成员列表中：查 extraHonorMap（已加载）或发起请求
    if (extraHonorMap[account]) return extraHonorMap[account];
    if (!fetchingAccountsRef.current.has(account)) {
      fetchingAccountsRef.current.add(account);
      general.axios.post("/getTeamUserDependentData", { accid: account }).then((res) => {
        const { status, data: d } = res.data;
        if (status.succeed) {
          // setState 触发重渲染，荣誉图标即时出现
          setExtraHonorMap((prev) => ({ ...prev, [account]: d }));
        }
      });
    }
    return null;
  // eslint-disable-next-line
  }, [isDisplayHonor, extraHonorMap, membersVersion, nickMembersVersion]);

  // ── NIM 消息监听：随组件生命周期绑定/解绑，防止多次挂载后重复触发 ────
  useEffect(() => {
    const handler = (msg) => {
      if (!msg.attach) return;
      const type = msg.attach.type;
      if (type === "updateTeam" && msg.to === tid) {
        getTeamInfo();
      }
      if (type === "removeTeamMembers" && msg.to === tid) {
        const accounts = msg.attach.accounts;
        for (const account of accounts) {
          if (account === localStorage.getItem("yx_account")) {
            Dialog.info("您已被踢出群聊", () => general.history.goBack());
          }
        }
      }
      if (type === "updateTeamMute" && msg.to === tid) {
        const myAccount = localStorage.getItem("yx_account");
        if (msg.attach.account === myAccount) {
          setIsMuted(!!msg.attach.mute);
        }
      }
      if ((type === "updateTeamMember" || type === "addTeamMembers") && msg.to === tid) {
        getYXNickMembers();
      }
    };
    nim.on("msg", handler);
    return () => nim.off("msg", handler);
  }, [nim, tid, getTeamInfo, getYXNickMembers]);

  // ── 首次进入初始化（群信息、成员、红包顶部） ─────────────────────────
  const initOnce = useCallback(() => {
    getTeamInfo();
    getYXNickMembers();
    getRedenvelopeTopMess();

    nim.getTeamInfo({ teamId: tid }).then((team) => {
      try {
        const ext = JSON.parse(team.ext || "{}");
        if (ext.yxAllowAt === "all") {
          nim.updateTeamInfo({ teamId: tid, ext: JSON.stringify({ yxAllowAt: "manager" }) });
        }
      } catch (e) {}
    });
  }, [tid, nim, getTeamInfo, getYXNickMembers, getRedenvelopeTopMess]);

  // ── Redux 订阅（红包更新） ────────────────────────────────────────────────
  // 放在这里是因为与红包顶部消息状态同属一处
  // eslint-disable-next-line
  const subscribeRedux = useCallback(() => {
    const unsubscribe = redux.subscribe(() => {
      const state = redux.getState();
      if (state.refreshRedEnvelopeData) {
        const data = state.refreshRedEnvelopeData;
        if (data.tid == tid) {
          if (parseInt(data.is_disappear_pop) === 1) {
            setVisible(false);
          } else {
            setVisible(true);
            setRedenvelopeTopMsg(data);
          }
        }
      }
    });
    return unsubscribe;
  }, [tid]);

  // ── 卸载时清理残留定时器 ─────────────────────────────────────────────
  useEffect(() => {
    return () => { clearTimeout(gotoChatTimerRef.current); };
  }, []);

  return {
    title,
    announcement,
    gameid,
    isAppointed,
    isDisplayHonor,
    isMuted,
    packSerialcode,
    packAlert,
    setPackAlert,
    visible,
    redenvelopeTopMsg,
    nickMembers: () => nickMembersRef.current,
    findAccountFilterByAccount,
    updateMsg,
    gotoChat,
    initOnce,
    subscribeRedux,
    getTeamInfo,
    getRedenvelopeTopMess,
  };
}
